import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { OriginProvider, useFeatureOrigin, useOriginNavigate, useRestoreOnReturn } from '@/lib/readiness/nav'
import { ReadinessScoreTooltip } from '@/components/readiness/ScoreTooltip'
import { ObjectivesMovedModal } from '@/components/readiness/ObjectivesMovedModal'
import { scoreBand } from '@/lib/readiness/score'
import { useTheme } from '@/contexts/ThemeContext'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { useReadinessStore, suggestedAssessmentName, ReviewGateError } from '@/stores/readinessStore'
import { allObjectives } from '@/lib/indexes/registry'
import { overviewStats, liveLevel, liveScore, computeIndexScore } from '@/lib/readiness/score'
import { assertScale } from '@/lib/readiness/scale'
import { RdPage, HeaderActions, ReminderBanner } from '@/components/readiness/shell'
import { PreliminaryNotice } from '@/components/readiness/PreliminaryNotice'
import { ReadinessOverTime, type ChartControls, type CoveragePoint, type ScopeMark } from '@/components/readiness/ReadinessOverTime'
import { projectionEnd } from '@/lib/readiness/chartRange'
import { CoverageChip, KpiTile, ObjectivesHistoryEstimate } from '@/components/readiness/ProgressParts'
import { CompareAssessments, type PairSource } from '@/components/readiness/CompareAssessments'
import { AssessmentRail } from '@/components/readiness/AssessmentRail'
import { ViewAllAssessmentsModal } from '@/components/readiness/ViewAllAssessmentsModal'
import { buildCompareColumns } from '@/lib/readiness/compare'
import { LIVE, pairKey, resolvePair, useComparePairStore, type ComparePair } from '@/lib/readiness/comparePair'
import { movesSince, paceOf, scopeGrowthFor } from '@/lib/readiness/progress'
import { useReadinessModule } from '@/lib/readiness/module'
import { fmtMonth } from '@/lib/readiness/projection'
import { SectionLabel } from '@/components/readiness/primitives'
import { ReviewSaveModal } from '@/components/readiness/ReviewSaveModal'
import { Term } from '@/components/readiness/Term'
import { formatDate } from '@/components/common/format'
import { LockedPreview } from '@/components/readiness/LockedPreview'
import { useIsLocked } from '@/stores/profileStore'

// Progress (I2 §7 as amended by I3 §3 and I6 Part A): readiness is tracked
// continuously; an assessment is a saved, point-in-time record you compare,
// report and export. Four blocks, in order (I6 §1.3): KPI tiles → "{index}
// readiness over time" with the projection → Compare assessments (figures
// band + matrix, with the assessment rail beside it) → Objectives · history
// and estimate. Only the Compare block and the chart rings respond to the
// From → To pair (§1.2).
//
// POC (I6 POC §5): a locked preview. Tiles and chart stay live; the gate starts
// at Compare assessments, i.e. everything that responds to From → To.

const DAY = 86_400_000

export default function Assessments() {
  const ctx = useReadiness()
  const m = useReadinessModule(ctx)
  const { projection } = m
  const { index, scope, orgId, indexId, actor } = ctx
  const { theme } = useTheme()
  const saveSnapshot = useReadinessStore((s) => s.saveSnapshot)
  const setRule = useReadinessStore((s) => s.setSnapshotRule)
  const track = useReadinessStore((s) => s.track)
  const [showSave, setShowSave] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const compareRef = useRef<HTMLElement>(null)
  const gateRef = useRef<HTMLDivElement>(null)
  const locked = useIsLocked('progress')
  const scale = assertScale(index)
  // Coming back from a detail page restores the chart state, the compared pair, scroll and focus (I5 §4.2, I6 Note 1).
  const restored = useRestoreOnReturn<{ controls: ChartControls; pair?: ComparePair }>()
  const origin = useFeatureOrigin('progress')
  const [showMoved, setShowMoved] = useState(false)
  // `To est. L3` is the default range; over 18 months the period auto-selects
  // Quarter (the user may override).
  const startedAt = useMemo(() => [...(scope.events ?? [])].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))[0]?.occurred_at ?? null, [scope.events])
  const autoPeriod = (range: ChartControls['range']): ChartControls['period'] => {
    if (range !== 'est' && range !== 'all') return 'month'
    const end = range === 'est' ? projectionEnd(projection, index.submission?.deadline ?? index.milestone?.date ?? null) ?? new Date() : new Date()
    return startedAt && end.getTime() - new Date(startedAt).getTime() > 548 * DAY ? 'quarter' : 'month'
  }
  const [controls, setControlsRaw] = useState<ChartControls>(() => restored?.controls ?? { period: 'month', range: projection ? 'est' : '6', series: 'score', custom: { from: '', to: '' } })
  const userPeriod = useRef(!!restored?.controls)
  const controlsRef = useRef(controls)
  controlsRef.current = controls
  const setControls = (c: ChartControls) => {
    if (c.period !== controls.period) userPeriod.current = true
    if (c.range !== controls.range && !userPeriod.current) c = { ...c, period: autoPeriod(c.range) }
    setControlsRaw(c)
  }
  useEffect(() => {
    if (!userPeriod.current) setControlsRaw((c) => ({ ...c, period: autoPeriod(c.range) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection?.summary.p80, startedAt])
  useEffect(() => {
    if (projection) track(orgId, indexId, { name: 'readiness.projection_viewed', all_l3_by: projection.summary.all_l3_by })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, indexId])

  // ── Compared pair (I6 §2.2, Note 1): one store read by the pickers, the
  // matrix, the rail, the chart rings and the Progress Report.
  const key = pairKey(orgId, indexId)
  const storedPair = useComparePairStore((s) => s.pairs[key])
  const setPairStore = useComparePairStore((s) => s.setPair)
  useEffect(() => {
    if (restored?.pair) setPairStore(key, restored.pair)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const snapshots = scope.snapshots
  const { fromId, toId } = resolvePair(restored?.pair && !storedPair ? restored.pair : storedPair, snapshots)
  const pairRef = useRef({ fromId, toId })
  pairRef.current = { fromId, toId }
  const getViewState = useCallback(() => ({ controls: controlsRef.current, pair: pairRef.current }), [])
  const setCompare = (f: string, t: string, source: PairSource) => {
    setPairStore(key, { fromId: f, toId: t })
    track(orgId, indexId, { name: 'readiness.compare_pair_changed', source })
  }

  // Plan versions (I3 §5, I5 §9): submitted versions only, at their submission
  // date; the draft start date carries no meaning and is never used.
  const planVersions = useMemo(() => m.plan.submissions.map((sub) => {
    const v = m.plan.versions.find((x) => x.id === sub.final_pdf_version_id) ?? null
    return { id: sub.id, n: sub.version, label: `CSA plan v${sub.version}`, created_at: sub.submitted_at, completeness: v?.completeness ?? 0, submitted_by: sub.user_id, csa_reference: sub.csa_reference }
  }), [m.plan.submissions, m.plan.versions])

  const openVersionLink = useOriginNavigate()
  const navigateToVersion = (n: number) => openVersionLink(`/readiness/submission${`?index=${indexId}`}&version=${n}#submission-v${n}`)

  const items = useMemo(() => allObjectives(index), [index])
  const stats = useMemo(() => overviewStats(index, scope.states), [index, scope.states])
  const liveLevelList = useMemo(() => items.map(({ objective }) => liveLevel(scope.states[objective.code])), [items, scope.states])
  const lastAssessment = snapshots[snapshots.length - 1] ?? null
  const liveIndex = useMemo(() => (scale === 'score_0_10' ? computeIndexScore(index, (c) => liveScore(scope.states[c])) : null), [index, scope.states, scale])
  const liveNumber = scale === 'levels' ? stats.score : (liveIndex?.overall ?? 0)
  const lastSync = scope.sync_runs.find((r) => r.status === 'succeeded')
  const liveNotMigrated = scope.settings.discovery_facts?.connected ? Math.max(0, (scope.settings.discovery_facts.asset_count || 0) - (scope.settings.discovery_facts.quantum_risk?.safe ?? 0)) : null
  const liveKnown = scope.settings.discovery_facts?.connected ? scope.settings.discovery_facts.asset_count : null

  // Coverage (§3.2, §4): assets known over time and one mark per scope_expanded sync.
  const events = useMemo(() => scope.events ?? [], [scope.events])
  const scopeMarks: ScopeMark[] = useMemo(() => {
    const byAt = new Map<string, ScopeMark>()
    events.filter((e) => e.event_type === 'scope_expanded').forEach((e) => byAt.set(e.occurred_at, { date: e.occurred_at, from: e.assets_known_from ?? 0, to: e.assets_known_to ?? 0 }))
    return Array.from(byAt.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [events])
  const coverage: CoveragePoint[] = useMemo(() => {
    const pts: CoveragePoint[] = [
      ...snapshots.filter((s) => s.discovery_summary).map((s) => ({ date: s.created_at, count: s.discovery_summary!.assets_total })),
      ...scopeMarks.flatMap((sm) => [{ date: new Date(new Date(sm.date).getTime() - 1).toISOString(), count: sm.from }, { date: sm.date, count: sm.to }]),
    ]
    if (liveKnown != null) pts.push({ date: new Date().toISOString(), count: liveKnown })
    return pts.sort((a, b) => a.date.localeCompare(b.date))
  }, [snapshots, scopeMarks, liveKnown])
  const ninety = useMemo(() => movesSince(events, new Date(Date.now() - 90 * DAY).toISOString()), [events])
  const pace = useMemo(() => (scale === 'levels' ? paceOf(index, events) : null), [index, events, scale])
  const baselineKnown = snapshots[0]?.discovery_summary?.assets_total ?? null

  const columns = useMemo(() => buildCompareColumns(index, snapshots, scope.states, { score: liveNumber, notMigrated: liveNotMigrated, known: liveKnown }), [index, snapshots, scope.states, liveNumber, liveNotMigrated, liveKnown])
  const fmt = (v: number) => (scale === 'levels' ? String(v) : v.toFixed(1))
  const q = `?index=${indexId}`

  const onSave = (input: { name: string; note: string | null; as_of: string }) => {
    try {
      const snap = saveSnapshot(orgId, indexId, { ...input, projection: projection ? { summary: projection.summary, gates: m.gates } : null }, actor)
      setShowSave(false)
      setCompare(fromId === LIVE ? snap.id : fromId, snap.id, 'picker')
    } catch (e) {
      if (!(e instanceof ReviewGateError)) throw e
    }
  }

  return (
    <OriginProvider origin={origin} getViewState={getViewState}>
    <RdPage
      title="Progress"
      subtitle={<>Readiness is tracked continuously. An <Term index={index} term="Assessment">assessment</Term> is a saved, point-in-time record you compare, report and export.</>}
      right={<HeaderActions ctx={ctx} onSaveSnapshot={() => setShowSave(true)} exportTo={`/readiness/progress/print${q}`} exportLabel="Export progress report" exportDisabled={locked} />}
      testId="readiness-progress"
    >
      <LockedPreview feature="progress" gateAt={gateRef} cardAt={0.34} title="Progress" body="Compare saved assessments, see how every objective moved, and get time-to-level estimates with confidence ranges.">
      <PreliminaryNotice index={index} />
      <ReminderBanner ctx={ctx} onReview={() => setShowSave(true)} />

      {/* 1 · KPI tiles (I3 §3.2): where we are now. Fixed windows, never the compared pair (I6 §1.2). */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="progress-kpis">
        {/* Score now (I5 §7): `43 · Foundational`, the band word opens the shared score tooltip. */}
        <KpiTile
          testId="kpi-score"
          label="Score now"
          value={scale === 'levels' ? (
            <>{fmt(liveNumber)} <span style={{ color: 'var(--rd-text-faint)' }}>·</span>{' '}
              <ReadinessScoreTooltip index={index} levels={liveLevelList} orgName={ctx.orgName} score={stats.score} surface="progress" orgId={orgId}>
                <span className="rd-band text-[18px]" style={{ color: scoreBand(stats.score).token }} data-testid="kpi-score-band">{scoreBand(stats.score).label}</span>
              </ReadinessScoreTooltip>
            </>
          ) : fmt(liveNumber)}
          sub={lastAssessment ? (() => { const d = Math.round((liveNumber - lastAssessment.score) * 10) / 10; return <span style={{ color: d > 0 ? 'var(--rd-status-ok-text)' : d < 0 ? 'var(--rd-status-warn)' : undefined }} data-testid="kpi-score-delta">{d > 0 ? '▲ +' : d < 0 ? '▼ ' : '= '}{d === 0 ? '' : d} since {lastAssessment.name}</span> })() : 'No earlier assessment'}
        />
        {/* Objectives moved (I5 §8): the whole tile opens the read-only quick view. */}
        <button type="button" className="rd-card min-h-[120px] p-5 text-left transition-colors hover:[border-color:var(--rd-border-control)]" onClick={() => setShowMoved(true)} data-testid="kpi-moved" aria-haspopup="dialog" data-origin-key="kpi-moved">
          <p className="rd-label">Objectives moved · last 90 d</p>
          <p className="rd-kpi mt-2">{ninety.up.length} <span style={{ color: 'var(--rd-status-ok-text)' }}>↑</span> · {ninety.down.length + ninety.coverage.length} <span style={{ color: 'var(--rd-status-warn)' }}>↓</span></p>
          <div className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
            {ninety.coverage.length ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <CoverageChip inline />
                {ninety.coverage.slice(0, 1).map((e) => { const g = scopeGrowthFor(events, e); return <span key={e.id} style={{ color: 'var(--rd-text-primary)' }}><span className="rd-mono rd-id">{e.objective_code}</span> {e.from_level} → {e.to_level}{g ? ` · +${g.toLocaleString()} assets` : ''}</span> })}
              </span>
            ) : ninety.down.length ? `${ninety.down.map((e) => e.objective_code).join(', ')} moved down` : 'no downward moves'}
          </div>
        </button>
        <KpiTile testId="kpi-coverage" label="Inventory coverage" value={liveKnown != null ? liveKnown.toLocaleString() : '—'} sub={liveKnown != null ? <>assets known from Discovery{baselineKnown != null ? <> · <span style={{ color: liveKnown > baselineKnown ? 'var(--rd-coverage)' : undefined }}>{liveKnown >= baselineKnown ? '+' : ''}{(liveKnown - baselineKnown).toLocaleString()} since baseline</span></> : null}</> : 'Discovery not connected'} />
        {/* Estimated all at L3 (I6 §3): the pace caption lives here, not in the compare block; it uses a fixed window. */}
        <KpiTile
          testId="kpi-est-l3"
          label={scale === 'levels' ? 'Estimated all objectives at L3' : 'Projection'}
          value={projection?.summary.all_l3_by ? fmtMonth(projection.summary.all_l3_by) : projection ? 'Not enough history' : '—'}
          valueColor="var(--rd-projection)"
          sub={projection ? (projection.summary.enough_history ? (
            <span className="block" data-testid="kpi-pace">Pace <span className="rd-mono" style={{ color: 'var(--rd-text-primary)' }}>{pace != null && pace >= 0 ? '+' : ''}{pace ?? '—'} / mo</span> over the last two quarters · all ≥ L2 by {fmtMonth(projection.summary.all_l2_by)}</span>
          ) : 'fewer than two completed transitions · rows run on QRI defaults') : `Not available for ${index.name} scores`}
        />
      </div>

      {/* 2 · {index} readiness over time (I2 §7.2, I3 §3.2): the compared pair is ringed (I6 §2.2). */}
      <div className="mb-5">
        <ReadinessOverTime index={index} events={events} snapshots={snapshots} planVersions={index.submission ? planVersions : []} onOpenVersion={(n) => navigateToVersion(n)} controls={controls} onControls={setControls} theme={theme} compared={{ fromId, toId }} onSelectNode={(id) => setCompare(id, toId, 'chart')} onSelectLive={() => setCompare(fromId, LIVE, 'chart')} projection={projection} coverage={coverage} scopeMarks={scopeMarks} height={260} />
      </div>

      {/* 3 · Compare assessments with the assessment rail beside it (I6 §1.3, §2). The card sets the row height; the rail never makes it taller. */}
      <div ref={gateRef} className="rd-compare-row mb-5" data-testid="compare-row">
        <CompareAssessments focusRef={compareRef} index={index} columns={columns} fromKey={fromId} toKey={toId} onSetPair={setCompare} events={events} evidence={scope.evidence} q={q} theme={theme} syncDate={lastSync ? lastSync.finished_at ?? lastSync.started_at : null} orgName={ctx.orgName} />

        {/* Assessment rail (I1 §9.4, I2 §7.2, I6 §2.2): Live · Saved assessments (one scrolling card) · Schedule. */}
        <AssessmentRail
          snapshots={snapshots}
          scale={scale}
          liveNumber={liveNumber}
          unconfirmed={stats.unconfirmed}
          fromId={fromId}
          toId={toId}
          defaultName={suggestedAssessmentName(new Date(), snapshots.map((x) => x.name))}
          fmt={fmt}
          q={q}
          onReviewSave={() => setShowSave(true)}
          onSetPair={(f, t) => setCompare(f, t, 'rail')}
          onViewAll={() => setShowAll(true)}
          schedule={
            <>
              <div className="mt-2 flex items-center justify-between text-[12.5px]">
                <span style={{ color: 'var(--rd-text-muted)' }}>Reminder</span>
                {/* Editable here; not a link to Settings while Settings is coming soon (I6 §15.1). */}
                <select aria-label="Assessment reminder" className="rd-input !w-auto !py-1 !text-[12.5px]" value={scope.settings.snapshot_rule} onChange={(e) => setRule(orgId, indexId, e.target.value as 'quarterly' | 'monthly')}>
                  <option value="quarterly">15th of each quarter-end month</option>
                  <option value="monthly">15th of each month</option>
                </select>
              </div>
              <p className="mt-2 flex justify-between text-[12.5px]"><span style={{ color: 'var(--rd-text-muted)' }}>Next reminder</span><span className="rd-mono">{scope.next_scheduled_snapshot ? formatDate(scope.next_scheduled_snapshot) : '—'}</span></p>
              <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>A reminder to save, never a constraint: levels can change at any time.</p>
              {(() => {
                const ms = index.submission ? { label: index.submission.label.replace(' plan', ''), date: index.submission.deadline } : index.milestone
                if (!ms) return null
                const days = Math.ceil((new Date(ms.date).getTime() - Date.now()) / 86_400_000)
                return (
                  <p className="mt-2 flex justify-between text-[12.5px]" style={{ color: days < 180 ? 'var(--rd-status-warn)' : undefined }}>
                    <span>{ms.label}</span>
                    <span className="rd-mono">{formatDate(ms.date)} · {days} days</span>
                  </p>
                )
              })()}
            </>
          }
          extra={scope.exports.length > 0 ? (
            <div className="rd-card shrink-0 p-4">
              <SectionLabel>Exports</SectionLabel>
              <ul className="mt-2 space-y-1.5 text-[12.5px]">
                {scope.exports.slice(0, 6).map((e) => (
                  <li key={e.id} className="flex justify-between gap-2">
                    <span className="truncate">{e.files.join(', ')}</span>
                    <span className="rd-mono shrink-0" style={{ color: 'var(--rd-text-muted)' }}>{formatDate(e.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        />
      </div>

      {/* 4 · Objectives · history and estimate (I6 §5): per-objective drill-down, tracking start → today, projection from today. */}
      {scale === 'levels' && projection && (
        <div className="mb-5">
          <ObjectivesHistoryEstimate index={index} events={events} theme={theme} q={q} projection={projection} states={scope.states} orgName={ctx.orgName} gateDetails={m.gateDetails} onSparkHover={(target) => track(orgId, indexId, { name: 'readiness.sparkline_hovered', target })} />
        </div>
      )}
      </LockedPreview>

      {showSave && !locked && <ReviewSaveModal index={index} scope={scope} actor={actor} onClose={() => setShowSave(false)} onSave={onSave} />}
      {showAll && (
        <ViewAllAssessmentsModal
          snapshots={snapshots}
          orgName={ctx.orgName}
          scale={scale}
          fromId={fromId}
          fmt={fmt}
          q={q}
          onClose={() => setShowAll(false)}
          onSetFrom={(id) => {
            setCompare(id, toId === id ? LIVE : toId, 'rail')
            setShowAll(false)
            // After the modal has returned focus to its opener, move it to the Compare card.
            window.setTimeout(() => compareRef.current?.focus({ preventScroll: true }), 0)
          }}
        />
      )}
      {showMoved && <ObjectivesMovedModal index={index} events={events} states={scope.states} orgName={ctx.orgName} orgId={orgId} indexId={indexId} theme={theme} onClose={() => setShowMoved(false)} />}
    </RdPage>
    </OriginProvider>
  )
}
